import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Send } from 'lucide-react';
import useContactForm from '../../hooks/useContactForm';

const SERVICE_OPTIONS = [
  { value: 'housing-loan', label: 'Housing Loan' },
  { value: 'education-loan', label: 'Education Loan' },
  { value: 'car-loan', label: 'Car Loan' },
  { value: 'property-mortgage', label: 'Property Mortgage' },
  { value: 'sme-loan', label: 'SME Loan' },
  { value: 'corporate-loan', label: 'Corporate Loan' },
  { value: 'real-estate', label: 'Real Estate Services' },
  { value: 'legal-advice', label: 'Legal Consultation' },
];

const INPUT_CLASS = 'border-gray-300 focus:border-cyan-500 focus:ring-cyan-500';

export const ContactForm = () => {
  const { formData, isSubmitting, setField, handleChange, handleSubmit } = useContactForm();

  return (
    <Card className="border-0 shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl">Send Us a Message</CardTitle>
        <CardDescription>Fill out the form and we'll get back to you shortly</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              name="name"
              data-testid="contact-name-input"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={handleChange}
              required
              className={INPUT_CLASS}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                name="email"
                data-testid="contact-email-input"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={handleChange}
                required
                className={INPUT_CLASS}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                name="phone"
                data-testid="contact-phone-input"
                type="tel"
                placeholder="+91 98765 43210"
                value={formData.phone}
                onChange={handleChange}
                required
                className={INPUT_CLASS}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="service">Service Interested In *</Label>
            <Select value={formData.service} onValueChange={(value) => setField('service', value)} required>
              <SelectTrigger className={INPUT_CLASS} data-testid="contact-service-select">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {SERVICE_OPTIONS.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              name="message"
              data-testid="contact-message-input"
              placeholder="Tell us about your requirements..."
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>

          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting}
            data-testid="contact-submit-btn"
            className="w-full bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white disabled:opacity-50"
          >
            <Send className="w-5 h-5 mr-2" />
            {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
